import { API_CONFIG } from '../config';

// Enhanced API Response wrapper
class ApiResponse {
  constructor(data, error = null, status = API_CONFIG.STATUS_CODES.OK, message = '') {
    this.data = data;
    this.error = error;
    this.status = status;
    this.success = !error;
    this.message = message;
    this.timestamp = new Date().toISOString();
  }
}

// Enhanced HTTP Client with authentication and error handling
class ApiClient {
  constructor(baseURL = API_CONFIG.BASE_URL) {
    this.baseURL = baseURL;
    this.authToken = null;
    this.refreshToken = null;
    this.interceptors = {
      request: [],
      response: []
    };
    
    // Load tokens from localStorage on initialization
    this.loadTokensFromStorage();
  }

  // Token management
  setTokens(accessToken, refreshToken = null) {
    this.authToken = accessToken;
    this.refreshToken = refreshToken;
    
    if (accessToken) {
      localStorage.setItem('authToken', accessToken);
    }
    if (refreshToken) {
      localStorage.setItem('refreshToken', refreshToken);
    }
  }

  clearTokens() {
    this.authToken = null;
    this.refreshToken = null;
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
  }

  loadTokensFromStorage() {
    this.authToken = localStorage.getItem('authToken');
    this.refreshToken = localStorage.getItem('refreshToken');
  }

  // Enhanced request method with retry logic and authentication
  async request(endpoint, options = {}) {
    const normalizedBase = String(this.baseURL || '').replace(/\/$/, '');
    const normalizedEndpoint = String(endpoint || '').startsWith('/') ? endpoint : `/${endpoint}`;
    const url = `${normalizedBase}${normalizedEndpoint}`;
    
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    // Add authentication header if token exists
    if (this.authToken && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${this.authToken}`;
    }

    // Add body for POST/PUT requests
    if (config.body && typeof config.body === 'object') {
      config.body = JSON.stringify(config.body);
    }

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), API_CONFIG.REQUEST_TIMEOUT_MS);

    try {
      const response = await fetch(url, { ...config, signal: controller.signal });
      const contentType = response.headers.get('content-type') || '';
      const isJson = contentType.includes('application/json');
      const data = isJson ? await response.json() : await response.text();

      if (!response.ok) {
        const message = isJson
          ? (data.message || data.error || `HTTP error! status: ${response.status}`)
          : `HTTP error! status: ${response.status}`;
        throw new ApiResponse(null, message, response.status, message);
      }

      return new ApiResponse(data, null, response.status, 'Request successful');
    } catch (error) {
      if (error instanceof ApiResponse) {
        throw error;
      }

      const message = error?.name === 'AbortError'
        ? 'Request timed out'
        : (error?.message || 'Network request failed');
      throw new ApiResponse(null, message, API_CONFIG.STATUS_CODES.INTERNAL_SERVER_ERROR, message);
    } finally {
      clearTimeout(timeout);
    }
  }

  get(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: API_CONFIG.HTTP_METHODS.GET });
  }

  post(endpoint, body, options = {}) {
    return this.request(endpoint, { ...options, method: API_CONFIG.HTTP_METHODS.POST, body });
  }

  put(endpoint, body, options = {}) {
    return this.request(endpoint, { ...options, method: API_CONFIG.HTTP_METHODS.PUT, body });
  }

  delete(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: API_CONFIG.HTTP_METHODS.DELETE });
  }
}

// Create singleton instance
const apiClient = new ApiClient();

export default apiClient;
